import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, KeyRound, UserSearch, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ROLES = [
  { id: "owner", label: "Property Owner", icon: Home, desc: "List your property for rent or sale" },
  { id: "buyer", label: "Buyer / Tenant", icon: UserSearch, desc: "Save preferences & submit requirements" },
  { id: "agent", label: "Agent", icon: KeyRound, desc: "List properties & manage leads" },
  { id: "visitor", label: "Visitor", icon: Eye, desc: "Just browsing for now" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", mobile: "", role: "buyer", agency_name: "", license_id: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setErr(res.error);
  };

  const input = "w-full bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-brand-500";

  return (
    <main className="min-h-screen flex items-center justify-center px-4 pt-32 pb-12 bg-ink">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-surface border border-neutral-200 shadow-card rounded-3xl p-8">
        <h1 className="font-display text-3xl font-bold text-neutral-900 mb-1">Create your account</h1>
        <p className="text-neutral-500 mb-6">Choose your role to get a tailored dashboard.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-3">
            {ROLES.map((r) => (
              <button type="button" key={r.id} onClick={() => setForm({ ...form, role: r.id })} data-testid={`role-${r.id}`}
                className={`flex items-start gap-3 p-4 rounded-2xl border text-left transition-colors ${
                  form.role === r.id ? "border-brand-500 bg-brand-500/5" : "border-neutral-200 hover:border-neutral-400"
                }`}>
                <r.icon className={`w-6 h-6 mt-0.5 ${form.role === r.id ? "text-brand-500" : "text-neutral-400"}`} />
                <div>
                  <div className="font-semibold text-neutral-900">{r.label}</div>
                  <div className="text-neutral-400 text-xs">{r.desc}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <input required placeholder="Full name" className={input} value={form.name} onChange={set("name")} data-testid="register-name" />
            <input required placeholder="Mobile number" className={input} value={form.mobile} onChange={set("mobile")} data-testid="register-mobile" />
            <input required type="email" placeholder="Email" className={input} value={form.email} onChange={set("email")} data-testid="register-email" />
            <input required type="password" placeholder="Password (min 6 chars)" className={input} value={form.password} onChange={set("password")} data-testid="register-password" />
          </div>

          {form.role === "agent" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Agency name" className={input} value={form.agency_name} onChange={set("agency_name")} data-testid="register-agency" />
              <input placeholder="License / ID proof" className={input} value={form.license_id} onChange={set("license_id")} data-testid="register-license" />
            </div>
          )}

          {err && <p className="text-red-500 text-sm" data-testid="register-error">{err}</p>}

          <button type="submit" disabled={loading} data-testid="register-submit-btn"
            className="w-full py-3 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl hover:shadow-soft transition-all active:scale-95 disabled:opacity-60">
            {loading ? "Creating account…" : "Create Account"}
          </button>
        </form>

        <p className="text-neutral-500 text-sm mt-6 text-center">
          Already have an account? <Link to="/login" className="text-brand-600 hover:underline font-medium" data-testid="link-login">Log in</Link>
        </p>
      </motion.div>
    </main>
  );
}
